import React from 'react';
import ActionButton from '~components/atoms/ActionButton';
import Table from '~components/molecules/Table';
import UtilFunctions from '~utils/functions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { CustomerAccFundTypeEnum, MetalTypeEnum, ModuleEnum, PermissionsEnum, TransactionStatusEnum } from '~utils/enums';

import * as Styles from './_collateralApprovalTableStyles.module.scss';
import { TUpdateCollateralStatusReduxParams, TUpdateDiscountStatusReduxParams } from '~types/feat/approval';
import combinedActions from '~redux/combinedActions';

const CollateralApprovalTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc:
        {
            dateTimeFormat,
            renderTransactionStatusChip,
        },
    } = UtilFunctions;

    const pendingProductListing = useAppSelector(state => state.approval.pendingCollateralListing.data);
    const getPendingProductIsLoading = useAppSelector(state => state.approval.loadings.getPendingCollateralListing);

    const allProducts = useAppSelector(state => state.gold.allProducts);

    const access = useAppSelector(state => state.auth.userDetails.access);
    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateCollateralState = (data: TUpdateCollateralStatusReduxParams) => dispatch(combinedActions.approvalSetTempUpdateCollateralState(data));
    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateCollateralStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateDiscountStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateCollateralState(data);
    };

    const handleRejectClick = (data: TUpdateDiscountStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateCollateralState(data);
    };

    const tableHeader = [
        'PRODUCT ID',
        'PRODUCT NAME',
        'COLLATERAL AMOUNT',
        'COLLATERAL END DATE',
        'CUSTOMER NAME',
        'CUSTOMER ID',
        'FUND TYPE',
        'CIF NUMBER',
        'STATUS',
        'DATE',
        'REMARKS',
        'ACTION',
    ];

    const renderActionButton = (id: number, status: TransactionStatusEnum) => {
        if (status === TransactionStatusEnum.Pending) {
            return (
                <div className={Styles['action-button-container']}>
                    {hasEditAccess && (
                        <ActionButton
                            label='Approve'
                            onClick={() => handleApproveClick({ id, status: TransactionStatusEnum.Approved })}
                            className={`${Styles['action-button']} ${Styles.approve}`}
                        />
                    )}

                    {hasEditAccess && (
                        <ActionButton
                            label='Reject'
                            onClick={() => handleRejectClick({ id, status: TransactionStatusEnum.Rejected })}
                            className={`${Styles['action-button']} ${Styles.reject}`}
                        />
                    )}
                </div>
            );
        }

        return '-';
    };

    const tableBody = pendingProductListing.map((item, index) => {
        const {
            amount,
            collateralId,
            collateralEndDate,
            customerName,
            customerId,
            fundType,
            customerCIFNo,
            marketValue,
            productId,
            productName,
            status,
            remarks,
            updatedAt,
        } = item;

        const product = allProducts.find(product => product.productId === productId);

        const isGold = product?.metalType === MetalTypeEnum.Gold;

        return {
            className: isGold ? Styles['gold-row'] : '',

            cells: [
                productId,
                productName,
                `RM ${Number(marketValue).toFixed(2)} (${amount}g)`,
                (
                    <div
                        key={`collateral-end-${index}`}
                        className={Styles['discount-type-date']}
                    >
                        {dateTimeFormat(collateralEndDate)}
                    </div>
                ),
                customerName,
                customerId,
                CustomerAccFundTypeEnum[fundType],
                customerCIFNo,
                renderTransactionStatusChip(status, TransactionStatusEnum[status]),
                (
                    <div
                        key={`updated-${index}`}
                        className={Styles['discount-type-date']}
                    >
                        {dateTimeFormat(updatedAt)}
                    </div>
                ),
                remarks || '-',
                renderActionButton(collateralId, status),
            ],
        };
    });

    return (
        <Table
            header={tableHeader}
            body={tableBody}
            isLoading={getPendingProductIsLoading}
        />
    );
};

export default CollateralApprovalTable;
